import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  Dashboard,
  ChildCare,
  EventNote,
  Group,
  LocationOn,
  Schedule,
  MonetizationOn,
  Menu,
  AccountCircle,
} from "@material-ui/icons";
import { palette } from "../../theme";

const DRAWER_WIDTH = 248;

const NAV_ITEMS = [
  { label: "Dashboard", to: "/", icon: <Dashboard /> },
  { label: "Kids", to: "/kids", icon: <ChildCare /> },
  { label: "Sessions", to: "/sessions", icon: <EventNote /> },
  { label: "Groups", to: "/groups", icon: <Group /> },
  { label: "Locations", to: "/locations", icon: <LocationOn /> },
  { label: "Schedule", to: "/schedule", icon: <Schedule /> },
  { label: "Calculator", to: "/calculator", icon: <MonetizationOn /> },
];

function Brand() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 2.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryLight})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        }}
      >
        <ChildCare />
      </Box>
      <Box>
        <Typography variant="h6" sx={{ lineHeight: 1.1, color: palette.text }}>
          OT App
        </Typography>
        <Typography variant="caption" sx={{ color: palette.textSecondary, fontWeight: 600 }}>
          Therapy made simple
        </Typography>
      </Box>
    </Box>
  );
}

function NavList({ onNavigate }) {
  const location = useLocation();
  return (
    <List sx={{ px: 1.5, flexGrow: 1 }}>
      {NAV_ITEMS.map((item) => {
        const selected =
          item.to === "/"
            ? location.pathname === "/"
            : location.pathname.startsWith(item.to);
        return (
          <ListItemButton
            key={item.to}
            component={Link}
            to={item.to}
            onClick={onNavigate}
            selected={selected}
            sx={{
              mb: 0.5,
              minHeight: 44,
              color: selected ? palette.primaryDark : palette.textSecondary,
              "&.Mui-selected": {
                backgroundColor: palette.primarySoft,
                "&:hover": { backgroundColor: "#CFF3F8" },
              },
              "&:hover": { backgroundColor: "#EEF6F8" },
            }}
          >
            <ListItemIcon
              sx={{ minWidth: 40, color: selected ? palette.primary : "#64748B" }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontWeight: selected ? 800 : 600 }}
            />
          </ListItemButton>
        );
      })}
    </List>
  );
}

function SidebarContent({ onNavigate }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Brand />
      <Divider sx={{ mx: 2, mb: 1 }} />
      <NavList onNavigate={onNavigate} />
      <Box sx={{ p: 2 }}>
        <Button
          component={Link}
          to="/sign-in"
          fullWidth
          variant="outlined"
          startIcon={<AccountCircle />}
          onClick={onNavigate}
        >
          {/* LoginProfile swaps this text by id after sign-in — keep the span. */}
          <span id="sign-button">Sign In</span>
        </Button>
      </Box>
    </Box>
  );
}

export default function AppLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Parity with the old navbar: hidden for the legacy "single" flow.
  if (location.pathname.includes("single-sign-up") || global.config.isSingle) {
    return <>{children}</>;
  }

  const closeMobile = () => setMobileOpen(false);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: palette.bg }}>
      {/* Mobile top bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: "flex", md: "none" },
          backgroundColor: "#fff",
          color: palette.text,
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            aria-label="Open navigation menu"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1, color: palette.text }}
          >
            <Menu />
          </IconButton>
          <Typography variant="h6">OT App</Typography>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={closeMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, borderRadius: 0 },
        }}
      >
        <SidebarContent onNavigate={closeMobile} />
      </Drawer>

      {/* Desktop sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRight: `1px solid ${palette.border}`,
            backgroundColor: "#FFFFFF",
            borderRadius: 0,
          },
        }}
        open
      >
        <SidebarContent />
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          pt: { xs: 8, md: 0 },
          minHeight: "100vh",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
